import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, Plus, Save, Trash2, ShieldCheck, X, Sparkles, Pencil, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { terms: "", enabled: true };

export default function AdminSynonyms() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        API.get("/admin/faq-synonyms").then(({ data }) => setItems(data))
            .catch(e => setError(apiError(e))).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const startNew = () => { setForm(EMPTY); setEditing("new"); };
    const startEdit = (s) => { setForm({ terms: (s.terms || []).join("\n"), enabled: s.enabled }); setEditing(s.id); };
    const cancel = () => { setEditing(null); setForm(EMPTY); };

    const parsedTerms = (form.terms || "").split("\n").map(x => x.trim()).filter(Boolean);
    const canSave = parsedTerms.length >= 2 && !saving;

    const save = async () => {
        setSaving(true);
        try {
            const payload = { terms: parsedTerms, enabled: form.enabled };
            if (editing === "new") { await API.post("/admin/faq-synonyms", payload); toast.success("Gruppo creato"); }
            else { await API.patch(`/admin/faq-synonyms/${editing}`, payload); toast.success("Gruppo aggiornato"); }
            cancel(); load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };
    const toggle = async (s) => {
        try { await API.patch(`/admin/faq-synonyms/${s.id}`, { terms: s.terms, enabled: !s.enabled }); load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const remove = async (id) => {
        try { await API.delete(`/admin/faq-synonyms/${id}`); toast.success("Gruppo eliminato"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2 text-muted-foreground">
                <Link to="/admin"><ArrowLeft className="size-4 mr-1" /> Panoramica admin</Link>
            </Button>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                <ShieldCheck className="size-3.5" /> Pannello amministratore
            </div>
            <div className="mt-1 flex items-center justify-between">
                <h1 className="font-serif text-3xl font-semibold inline-flex items-center gap-2" data-testid="admin-synonyms-title">
                    <Sparkles className="size-6 text-primary" /> Sinonimi condominiali
                </h1>
                {editing === null && <Button onClick={startNew} data-testid="admin-synonyms-new"><Plus className="size-4 mr-2" /> Nuovo gruppo</Button>}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
                Ogni gruppo è <strong>bidirezionale</strong>: se scrivi «affittuario» e «inquilino» insieme, la ricerca troverà l'uno cercando l'altro.
            </p>

            {error && <Alert variant="destructive" className="mt-4"><AlertDescription>{error}</AlertDescription></Alert>}

            {editing !== null && (
                <Card className="mt-6 border-primary/30" data-testid="admin-synonyms-form">
                    <CardContent className="pt-6 space-y-4">
                        <div className="space-y-2">
                            <Label>Termini del gruppo * <span className="text-xs text-muted-foreground">(uno per riga, min 2)</span></Label>
                            <Textarea value={form.terms} onChange={e => setForm({ ...form, terms: e.target.value })} rows={5} data-testid="synonym-terms" placeholder={"affittuario\ninquilino\nconduttore"} />
                            <p className="text-xs text-muted-foreground">{parsedTerms.length} termini validi</p>
                        </div>
                        <div className="flex justify-end gap-2 pt-2 border-t">
                            <Button variant="ghost" onClick={cancel}><X className="size-4 mr-1" /> Annulla</Button>
                            <Button onClick={save} disabled={!canSave} data-testid="synonym-save">
                                {saving ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Save className="size-4 mr-2" />} Salva
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="mt-6">
                {loading ? <div className="text-center py-16"><Loader2 className="size-6 mx-auto animate-spin text-muted-foreground" /></div>
                    : items.length === 0 ? (
                        <Card className="border-dashed border-2 bg-muted/30"><CardContent className="py-14 text-center">
                            <Sparkles className="size-10 mx-auto text-muted-foreground/40" />
                            <p className="mt-3 text-muted-foreground">Ancora nessun gruppo sinonimi. Iniziamo con «affittuario / inquilino»?</p>
                            {editing === null && <Button onClick={startNew} className="mt-5"><Plus className="size-4 mr-2" /> Crea il primo gruppo</Button>}
                        </CardContent></Card>
                    ) : (
                        <div className="space-y-3" data-testid="admin-synonyms-list">
                            {items.map(s => (
                                <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 flex items-start justify-between gap-3" data-testid={`syn-${s.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                                            <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${s.enabled ? "bg-emerald-600 text-white border-emerald-600" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                                                {s.enabled ? "Attivo" : "Disattivato"}
                                            </span>
                                            <span className="text-xs text-muted-foreground">{s.terms.length} termini</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {s.terms.map((t, i) => (
                                                <span key={i} className="inline-flex items-center rounded-full bg-primary/10 text-primary text-xs font-medium px-2.5 py-1 border border-primary/20">
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <Button size="sm" variant="ghost" onClick={() => toggle(s)} title={s.enabled ? "Disattiva" : "Attiva"}>
                                            {s.enabled ? <ToggleRight className="size-4 text-emerald-600" /> : <ToggleLeft className="size-4" />}
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => startEdit(s)}><Pencil className="size-4" /></Button>
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-destructive hover:text-destructive"><Trash2 className="size-4" /></Button></AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Eliminare questo gruppo?</AlertDialogTitle>
                                                    <AlertDialogDescription>Operazione irreversibile.</AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                                                    <AlertDialogAction onClick={() => remove(s.id)} className="bg-destructive text-destructive-foreground">Elimina</AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
            </div>
        </div>
    );
}
